<template>
  <div class="rich-text-editor">
    <div :class="{ 'quill-editor': true, 'ql-disabled': disabled }">
      <quill-editor
        ref="quillEditorInstance"
        :value="value"
        :options="computedEditorOptions"
        :disabled="disabled"
        @input="onEditorInput"
        @ready="onEditorReady" />
    </div>

    <!-- Image Selector Modal -->
    <ImageSelector
      v-if="showImageModal"
      :files="[]"
      :show-modal="showImageModal"
      @closeModal="closeImageModal"
      @submit="handleImageUpload" />
  </div>
</template>

<script>
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';

import { quillEditor } from 'vue-quill-editor';
import ImageSelector from '@/components/shared/ImageSelector.vue';
import { toolbarSettings } from '@/constants/quill-editor';

export default {
  name: 'RichTextEditor',
  components: {
    quillEditor,
    ImageSelector,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Wprowadź treść...',
    },
    disabledMessage: {
      type: String,
      default: 'Aby edytować treść, najpierw wypełnij wymagane pola.',
    },
    editorOptions: {
      type: Object,
      default: () => ({}),
    },
    editorRef: {
      type: String,
      default: 'editor',
    },
    // Allow customization of toolbar
    toolbar: {
      type: Array,
      default: () => toolbarSettings,
    },
  },
  data() {
    return {
      showImageModal: false,
      inlineImages: [],
    };
  },
  computed: {
    computedEditorOptions() {
      const self = this;
      return {
        placeholder: this.disabled ? this.disabledMessage : this.placeholder,
        modules: {
          toolbar: {
            container: this.toolbar,
            handlers: {
              image: function () {
                self.imageHandler();
              },
            },
          },
        },
        theme: 'snow',
        ...this.editorOptions,
      };
    },
  },
  watch: {
    disabled(newVal) {
      // Update placeholder when disabled state changes
      this.$nextTick(() => {
        const quillInstance = this.$refs.quillEditorInstance?.quill;
        if (quillInstance) {
          const placeholder = newVal ? this.disabledMessage : this.placeholder;
          quillInstance.root.setAttribute('data-placeholder', placeholder);
        }
      });
    },
  },
  methods: {
    onEditorInput(content) {
      this.$emit('input', content);
    },
    onEditorReady(quill) {
      // Ensure editor is focusable even when empty
      if (quill) {
        const placeholder = this.disabled ? this.disabledMessage : this.placeholder;
        quill.root.setAttribute('data-placeholder', placeholder);
        quill.root.addEventListener('click', () => {
          if (!this.disabled) {
            quill.focus();
          }
        });
      }
      this.$emit('ready', quill);
    },
    imageHandler() {
      if (this.disabled) return;

      // Open the image selector modal
      this.showImageModal = true;
    },
    closeImageModal() {
      this.showImageModal = false;
    },
    handleImageUpload(fileList) {
      if (fileList.length > 0) {
        const file = fileList[0];

        // Get the Quill instance
        const quillInstance = this.$refs.quillEditorInstance.quill;

        if (quillInstance) {
          try {
            // Get cursor position
            const range = quillInstance.getSelection();
            const position = range ? range.index : 0;

            // Create unique identifier for this image
            const imageId = `inline-image-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

            // Insert image into editor using the preview (base64) URL
            quillInstance.insertEmbed(position, 'image', file.preview);

            // Add data attribute to the inserted image for tracking
            const imageEl = quillInstance.root.querySelector(`img[src="${file.preview}"]`);
            if (imageEl) {
              imageEl.setAttribute('data-inline-id', imageId);
              imageEl.setAttribute('data-filename', file.fileName);
            }

            // Move cursor after the image
            quillInstance.setSelection(position + 1);

            // Store image data and emit event
            const imageData = {
              id: imageId,
              file: file.file,
              preview: file.preview,
              fileName: file.fileName,
            };

            this.inlineImages.push(imageData);
            this.$emit('image-uploaded', imageData);
            this.$emit('images-changed', this.inlineImages);
          } catch (error) {
            console.error('Error inserting image:', error);
            this.$emit('error', {
              type: 'image-insertion',
              message: 'Nie udało się wstawić obrazu do edytora.',
              error,
            });
          }
        }
      }

      this.closeImageModal();
    },
    // Public method to get all inline images
    getInlineImages() {
      return this.inlineImages;
    },
    // Public method to clear inline images
    clearInlineImages() {
      this.inlineImages = [];
      this.$emit('images-changed', this.inlineImages);
    },
    // Public method to set inline images (for editing existing content)
    setInlineImages(images) {
      this.inlineImages = images || [];
      this.$emit('images-changed', this.inlineImages);
    },
    // Public method to replace base64 images with actual URLs after upload
    replaceImageUrls(urlMapping) {
      const quillInstance = this.$refs.quillEditorInstance.quill;
      if (quillInstance) {
        Object.entries(urlMapping).forEach(([imageId, newUrl]) => {
          const imageEl = quillInstance.root.querySelector(`img[data-inline-id="${imageId}"]`);
          if (imageEl) {
            imageEl.src = newUrl;
          }
        });

        // Update the content
        this.$emit('input', quillInstance.root.innerHTML);
      }
    },
  },
};
</script>

<style lang="scss">
// Rich Text Editor Styles - Global to override Quill defaults
.rich-text-editor {
  // Normal editor styling
  .ql-editor {
    min-height: 120px;

    &.ql-blank::before {
      font-style: italic;
      color: #aaa;
      pointer-events: none;
    }

    &:focus {
      outline: none;
    }

    // Styling for images in editor
    img {
      max-width: 100%;
      height: auto;
      border-radius: 4px;
      margin: 10px 0;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

      &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        cursor: pointer;
      }
    }
  }

  .ql-container {
    border-bottom: 1px solid #ccc;
  }

  .ql-toolbar {
    border-top: 1px solid #ccc;
    border-left: 1px solid #ccc;
    border-right: 1px solid #ccc;
  }
}

// Disabled state styling - More specific selectors
.rich-text-editor .ql-disabled {
  .ql-toolbar {
    background-color: #f8f9fa !important;
    opacity: 0.6;
    cursor: not-allowed;
    border-color: #dee2e6 !important;

    .ql-picker,
    .ql-formats .ql-picker {
      pointer-events: none;
    }

    button {
      cursor: not-allowed;
      opacity: 0.5;
      pointer-events: none;
    }
  }

  .ql-container {
    background-color: #f8f9fa !important;
    cursor: not-allowed;
    border-color: #dee2e6 !important;

    .ql-editor {
      background-color: #f8f9fa !important;
      color: #6c757d !important;
      cursor: not-allowed;
      filter: grayscale(0.8);
      min-height: 120px;

      &.ql-blank::before {
        color: #6c757d !important;
        font-style: italic;
        opacity: 1;
        pointer-events: none;
      }

      // Override any content styling when disabled
      * {
        color: #6c757d !important;
        cursor: not-allowed;
      }
    }
  }
}
</style>
